// src/App.tsx
import React, { useEffect } from "react";
import {
  Routes,
  Route,
  Outlet,
  useNavigate,
  useLocation,
} from "react-router-dom";
import AppBootstrapper from "./components/AppBootstrapper";
import PrivateRoute from "./components/PrivateRoute";
import OnboardedRoute from "./components/OnboardedRoute";
import LoadingIndicator from "./components/LoadingIndicator";
import { isVCSubdomain } from "./utils/subdomainDetection";
import VCRouteGuard from "./components/VCRouteGuard";
import { useAuth } from "./contexts/AuthContext";
import { usePageTracking } from "./hooks/usePageTracking";

import Login from "./pages/Login";
import VCLogin from "./pages/VCLogin";
import Signup from "./pages/Signup";
import VCSignup from "./pages/VCSignup";
import OnboardingFlow from "./pages/onboarding/OnboardingFlow";
import VCOnboardingFlow from "./pages/vc-onboarding/VCOnboardingFlow";
import VCDashboard from "./pages/VCDashboard";
import AdminDashboard from "./pages/AdminDashboard";
import InviteVC from "./pages/admin/InviteVC";
import AdminNewslettersPage from "./pages/admin/AdminNewslettersPage";
import NewsletterDetailPage from "./pages/admin/NewsletterDetailPage";
import NewsletterEditionEditor from "./pages/admin/NewsletterEditionEditor";
import CreatePromoCode from "./pages/admin/CreatePromoCode";
import AdminBlogPage from "./pages/admin/AdminBlogPage";
import BlogArticleEditor from "./pages/admin/BlogArticleEditor";
import OnboardingComplete from "./pages/onboarding/OnboardingComplete";
import PaymentCancelled from "./pages/payments/PaymentCancelled";
import PaymentSuccessHandler from "./pages/payments/PaymentSuccessHandler";
import Dashboard from "./pages/Dashboard";
import EmailUpdates from "./pages/EmailUpdates";
import EmailUpdatesLayout from "./pages/EmailUpdatesLayout";
import Insights from "./pages/Insights";
import Profile from "./pages/Profile";
import Integrations from "./pages/Integrations";
import Contacts from "./pages/Contacts";
import Settings from "./pages/Settings";
import Pricing from "./pages/Pricing";
import Layout from "./components/Layout";
import UpdateDetailEnhanced from "./pages/UpdateDetailEnhanced";
import SendSessionDetail from "pages/SendSessionDetail";
import WorkflowManager from "./pages/WorkflowManager";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import Update from "./pages/Update";
import AuthCallback from "pages/auth/AuthCallback";
import ComposioCallback from "pages/oauth/ComposioCallback";
import Debug from "./pages/Debug";
import ComposeUpdatePage from "./pages/compose/ComposeUpdatePage";
import ChooseRecipientsPage from "./pages/compose/ChooseRecipientsPage";
import ScheduleReviewPage from "./pages/compose/ScheduleReviewPage";
import AdminRouteGuard from "./components/AdminRouteGuard";
import Fundraise from "./pages/Fundraise";
import DataRooms from "./pages/DataRooms";
import DataRoomDetail from "./pages/DataRoomDetail";
import Decks from "./pages/Decks";
import DeckDetail from "./pages/DeckDetail";
import PublicDeckView from "./pages/PublicDeckView";
import PublicDataRoomView from "./pages/PublicDataRoomView";
import PublicPipelineView from "./pages/PublicPipelineView";
import ClaimPipelineInvite from "./pages/ClaimPipelineInvite";
import PendingCollabClaimModal from "./components/fundraise/PendingCollabClaimModal";
import { FeatureGate } from "./components/common";
import { DocsLayout } from "./components/docs/layout/DocsLayout";
import { DocsHome } from "./pages/docs/DocsHome";
import { DocsArticle } from "./pages/docs/DocsArticle";

/**
 * Application-wide routes, assumes routing context is provided by index.tsx.
 */
const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={isVCSubdomain() ? <VCLogin /> : <Login />}
      />
      <Route path="/:vcSlug/signup" element={<Signup isVCInvite={true} />} />
      <Route
        path="/signup"
        element={
          <VCRouteGuard>
            {isVCSubdomain() ? <VCSignup /> : <Signup />}
          </VCRouteGuard>
        }
      />
      <Route
        path="/signup/vc"
        element={
          <VCRouteGuard>
            <VCSignup />
          </VCRouteGuard>
        }
      />

      <Route path="/auth/v1/callback" element={<AuthCallback />} />

      {/*
        Composio connect-popup landing page. Public and outside <Layout> on
        purpose: it renders no app chrome and must close itself rather than
        redirect to /login. See pages/oauth/ComposioCallback.tsx.
      */}
      <Route path="/oauth/composio/callback" element={<ComposioCallback />} />

      {/* Onboarding flow */}
      <Route
        path="/onboarding"
        element={
          <PrivateRoute>
            <OnboardingFlow />
          </PrivateRoute>
        }
      />

      {/* VC Onboarding flow */}
      <Route
        path="/onboarding/vc"
        element={
          <PrivateRoute>
            <VCRouteGuard>
              <VCOnboardingFlow />
            </VCRouteGuard>
          </PrivateRoute>
        }
      />

      {/* Public legal pages */}
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />

      {/* Public documentation */}
      <Route path="/docs" element={<DocsLayout />}>
        <Route index element={<DocsHome />} />
        <Route path=":section" element={<DocsArticle />} />
        <Route path=":section/:slug" element={<DocsArticle />} />
      </Route>

      {/* Slug-based deck sharing (new URL format) */}
      <Route path="/decks/:businessSlug/:linkSlug" element={<PublicDeckView />} />

      {/* Slug-based data room sharing (new URL format) */}
      <Route path="/data-room/:businessSlug/:linkSlug" element={<PublicDataRoomView />} />

      {/* Legacy deck/data room routes — kept for backward compatibility */}
      <Route path="/deck/:token" element={<PublicDeckView />} />
      <Route path="/room/:token" element={<PublicDataRoomView />} />

      {/* Public fundraise pipeline share links (read-only public view) */}
      <Route
        path="/pipeline/:businessSlug/:linkSlug"
        element={<PublicPipelineView />}
      />
      <Route path="/pipeline/:token" element={<PublicPipelineView />} />

      {/* Pipeline collaborator-invite claim: requires auth so PrivateRoute
          bounces unauthed visitors to /login with a return URL pointing
          back here. After login they're returned and the page calls
          POST /api/fundraise/pipelines/claim before redirecting to
          /fundraise. */}
      <Route
        path="/fundraise/claim/:token"
        element={
          <PrivateRoute>
            <ClaimPipelineInvite />
          </PrivateRoute>
        }
      />

      {/* Stripe callback for onboarding completion */}
      <Route
        path="/onboarding-complete"
        element={
          <PrivateRoute>
            {/* Skip OnboardedRoute here to avoid redirect loop */}
            <OnboardingComplete />
          </PrivateRoute>
        }
      />

      {/* Payment cancelled/error page */}
      <Route
        path="/payment-cancelled"
        element={
          <PrivateRoute>
            <PaymentCancelled />
          </PrivateRoute>
        }
      />

      {/* Payment success handler - redirects to original page with modal */}
      <Route
        path="/payment-success-handler"
        element={
          <PrivateRoute>
            <PaymentSuccessHandler />
          </PrivateRoute>
        }
      />

      {/* VC Dashboard */}
      <Route
        path="/vc-dashboard/*"
        element={
          <PrivateRoute>
            <VCRouteGuard>
              <VCDashboard />
            </VCRouteGuard>
          </PrivateRoute>
        }
      />

      {/* Protected main application routes, nested under layout for shared UI */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <OnboardedRoute>
              <Layout>
                <Outlet />
              </Layout>
            </OnboardedRoute>
          </PrivateRoute>
        }
      >
        <Route
          index
          element={
            <div className="p-3 sm:p-6">
              <Dashboard />
            </div>
          }
        />
        <Route
          path="insights"
          element={
            <div className="p-3 sm:p-6">
              <Insights />
            </div>
          }
        />
        <Route path="email-updates" element={<EmailUpdatesLayout />}>
          <Route path="session/:sessionId" element={<SendSessionDetail />} />
          <Route path=":updateId" element={<UpdateDetailEnhanced />} />
        </Route>
        <Route
          path="workflows"
          element={
            <div className="p-3 sm:p-6">
              <FeatureGate feature="automations">
                <WorkflowManager />
              </FeatureGate>
            </div>
          }
        />
        <Route
          path="settings"
          element={
            <div className="p-3 sm:p-6">
              <Settings />
            </div>
          }
        />
        <Route
          path="admin"
          element={
            <AdminRouteGuard>
              <div className="p-3 sm:p-6">
                <AdminDashboard />
              </div>
            </AdminRouteGuard>
          }
        />
        <Route
          path="admin/invite-vc"
          element={
            <AdminRouteGuard>
              <div className="p-3 sm:p-6">
                <InviteVC />
              </div>
            </AdminRouteGuard>
          }
        />
        <Route
          path="admin/newsletters"
          element={
            <AdminRouteGuard>
              <div className="p-3 sm:p-6">
                <AdminNewslettersPage />
              </div>
            </AdminRouteGuard>
          }
        />
        <Route
          path="admin/newsletters/:id"
          element={
            <AdminRouteGuard>
              <div className="p-3 sm:p-6">
                <NewsletterDetailPage />
              </div>
            </AdminRouteGuard>
          }
        />
        <Route
          path="admin/blog"
          element={
            <AdminRouteGuard>
              <div className="p-3 sm:p-6">
                <AdminBlogPage />
              </div>
            </AdminRouteGuard>
          }
        />
        <Route
          path="admin/blog/:id"
          element={
            <AdminRouteGuard>
              <div className="p-3 sm:p-6">
                <BlogArticleEditor />
              </div>
            </AdminRouteGuard>
          }
        />
        <Route
          path="admin/newsletters/:id/editions/:eid"
          element={
            <AdminRouteGuard>
              <div className="p-3 sm:p-6">
                <NewsletterEditionEditor />
              </div>
            </AdminRouteGuard>
          }
        />
        <Route
          path="admin/promo/create"
          element={
            <AdminRouteGuard>
              <div className="p-3 sm:p-6">
                <CreatePromoCode />
              </div>
            </AdminRouteGuard>
          }
        />
        <Route
          path="profile"
          element={
            <div className="p-3 sm:p-6">
              <Profile />
            </div>
          }
        />
        <Route path="integrations" element={<Integrations />} />
        <Route path="contacts" element={<Contacts />} />
        <Route
          path="fundraise"
          element={
            <div className="p-3 sm:p-6">
              <Fundraise />
            </div>
          }
        />
        <Route
          path="data-rooms"
          element={
            <div className="p-3 sm:p-6">
              <FeatureGate feature="dataRoom">
                <DataRooms />
              </FeatureGate>
            </div>
          }
        />
        <Route
          path="data-rooms/:id"
          element={
            <div className="p-3 sm:p-6">
              <FeatureGate feature="dataRoom">
                <DataRoomDetail />
              </FeatureGate>
            </div>
          }
        />
        <Route
          path="decks"
          element={
            <div className="p-3 sm:p-6">
              <FeatureGate feature="decks">
                <Decks />
              </FeatureGate>
            </div>
          }
        />
        <Route
          path="decks/:id"
          element={
            <div className="p-3 sm:p-6">
              <FeatureGate feature="decks">
                <DeckDetail />
              </FeatureGate>
            </div>
          }
        />
        <Route
          path="pricing"
          element={
            <div className="p-3 sm:p-6">
              <Pricing />
            </div>
          }
        />
        {process.env.NODE_ENV !== "production" && (
          <Route
            path="debug"
            element={
              <div className="p-3 sm:p-6">
                <Debug />
              </div>
            }
          />
        )}
        <Route
          path="update"
          element={
            <div className="p-3 sm:p-6">
              <Update />
            </div>
          }
        />
        <Route
          path="updates/new"
          element={
            <div className="p-3 sm:p-6">
              <ComposeUpdatePage />
            </div>
          }
        />
        <Route
          path="updates/recipients"
          element={
            <div className="p-3 sm:p-6">
              <ChooseRecipientsPage />
            </div>
          }
        />
        <Route
          path="updates/schedule"
          element={
            <div className="p-3 sm:p-6">
              <ScheduleReviewPage />
            </div>
          }
        />
      </Route>

      {/* Fallback loading */}
      <Route path="*" element={<LoadingIndicator />} />
    </Routes>
  );
};

/**
 * Root application component with global layout.
 * ReduxProvider, BrowserRouter, AuthProvider, and PaymentProvider
 * are configured in index.tsx, so only the bootstrapper and navbar
 * are rendered here.
 */
const App: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, loading, onboardingLoading, isOnboarded, userRole } =
    useAuth();

  // Send a GA4 page_view on every route change
  usePageTracking();

  useEffect(() => {
    const isVC = isVCSubdomain();
    const currentPath = location.pathname;

    // Wait for BOTH auth AND onboarding to be determined before doing routing
    if (loading || (isAuthenticated && onboardingLoading)) {
      return;
    }

    // Handle VC subdomain routing
    if (isVC) {
      // Handle root path routing based on auth state
      if (currentPath === "/") {
        if (!isAuthenticated) {
          // Not authenticated -> go to signup
          navigate("/signup", { replace: true });
        } else if (userRole === "vc") {
          // Authenticated VC user
          if (!isOnboarded) {
            // Not onboarded -> go to VC onboarding
            navigate("/onboarding/vc", { replace: true });
          } else {
            // Onboarded -> go to VC dashboard
            navigate("/vc-dashboard", { replace: true });
          }
        } else {
          // Non-VC user on VC subdomain (shouldn't happen, but fallback)
          navigate("/signup", { replace: true });
        }
      }
      // Redirect /signup/vc to /signup on VC subdomain
      else if (currentPath === "/signup/vc") {
        navigate("/signup", { replace: true });
      }
      // Redirect /onboarding to /onboarding/vc
      else if (currentPath === "/onboarding") {
        navigate("/onboarding/vc", { replace: true });
      }
    }

    // Handle authenticated VC users who land on non-VC routes (post-login navigation)
    if (isAuthenticated && userRole === "vc" && !onboardingLoading) {
      // Skip navigation if already on appropriate pages
      if (
        currentPath === "/vc-dashboard" ||
        currentPath.startsWith("/vc-dashboard/") ||
        currentPath.startsWith("/onboarding") ||
        currentPath.startsWith("/settings") ||
        currentPath.startsWith("/signup") ||
        currentPath === "/login"
      ) {
        return;
      }

      // VC users who land on other routes should be redirected appropriately
      console.log("App.tsx: VC user on unexpected route, redirecting", {
        currentPath,
        isOnboarded,
        userRole,
      });

      if (!isOnboarded) {
        navigate("/onboarding/vc", { replace: true });
      } else {
        navigate("/vc-dashboard", { replace: true });
      }
    }
  }, [
    navigate,
    location.pathname,
    isAuthenticated,
    loading,
    onboardingLoading,
    isOnboarded,
    userRole,
  ]);

  return (
    <AppBootstrapper>
      {/* Show loading indicator on VC subdomain root while determining auth AND onboarding */}
      {isVCSubdomain() &&
      location.pathname === "/" &&
      (loading ||
        (isAuthenticated && (onboardingLoading || isOnboarded === null))) ? (
        <LoadingIndicator />
      ) : (
        <>
          <AppRoutes />
          {/* Watches localStorage for a pending fundraise collaborator-claim
              token (set by PublicPipelineView when an unauthed visitor hits
              Sign up to collaborate) and surfaces an accept-invite modal as
              soon as auth resolves. Renders nothing while there's no pending
              token. */}
          <PendingCollabClaimModal />
        </>
      )}
    </AppBootstrapper>
  );
};

export default App;
